import { effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from './toast/snackbar.service';

@Injectable({ providedIn: 'root' })
export class InternetStatusService {
  private readonly _isOnline = signal(navigator.onLine);
  private readonly reloadDelaySec: number = 10;
  public readonly isOnline = this._isOnline.asReadonly();
  public readonly snackbar = inject(SnackbarService);

  private wasOffline = false;
  private reloadTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private translate: TranslateService,
    private readonly ngZone: NgZone,
  ) {}

  public start(): void {
    window.addEventListener('online', () => this._isOnline.set(true));
    window.addEventListener('offline', () => this._isOnline.set(false));

    effect(() => {
      const online = this._isOnline();

      if (!online) {
        this.wasOffline = true;
        this.clearRestartTimeout();
        this.snackbar.open(this.translate.instant('general.internetStatus.lost'), 0);
      } else if (this.wasOffline) {
        const ref = this.snackbar.open(
          this.translate.instant('general.internetStatus.restored', { seconds: this.reloadDelaySec }),
          0,
          this.translate.instant('general.internetStatus.cancelReload'),
        );

        ref.onAction().subscribe(() => {
          this.clearRestartTimeout();
        });

        this.ngZone.runOutsideAngular(() => {
          this.reloadTimeout = setTimeout(() => {
            location.reload();
          }, this.reloadDelaySec * 1000);
        });
      }
    });
  }

  private clearRestartTimeout(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
      this.reloadTimeout = null;
    }
  }
}
